import type { PracticeTaskComment, PracticeTaskMaterial } from "@wmf/shared/practiceReview";
import type { MetronomeSpeedUpPreset, MetronomeSubdivisionPreset } from "@wmf/shared/metronomePractice";
import { get, send } from "./api.js";

export type PracticeBeatMode = "accent" | "normal" | "muted";

export interface MetronomePracticePreset {
  bpm: number;
  signature: "2/4" | "3/4" | "4/4" | "5/4" | "6/8" | "7/8";
  beatModes: PracticeBeatMode[];
  /** Missing/null on older assignments means constant tempo. */
  speedUp?: MetronomeSpeedUpPreset | null;
  /** Missing/null on older assignments means quarter notes with no extra clicks. */
  subdivision?: MetronomeSubdivisionPreset | null;
  /** Lesson that created the assignment, when it came from a lesson write-up. */
  sourceLessonId: string | null;
}

export interface PracticeTodo {
  id: string;
  /** Present only when the source note is currently published for this learner. */
  sourceLessonId?: string | null;
  title: string;
  description: string | null;
  completed: boolean;
  completedAt: string | null;
  readyForReviewAt?: string | null;
  comments?: PracticeTaskComment[];
  materials?: PracticeTaskMaterial[];
  createdAt: string;
  /**
   * When this stopped being work anybody is setting.
   *
   * The task is kept because the child practised it, or a tutor linked it to a
   * goal — it is their record of what they did. Null for live work, and
   * optional because an older API answering during a rolling deploy sends no
   * such field at all; absent has to read as live, never as withdrawn.
   */
  withdrawnAt?: string | null;
  repertoireId: string | null;
  repertoireTitle: string | null;
  repertoireComposer: string | null;
  /** Tutor-set guide for a normal practice day; never a hard timer limit. */
  suggestedMinutes: number | null;
  /** Practice linked to this task across all logged focus segments. */
  practiceSeconds: number;
  /** A playable groove set as this task's work, where there is one. */
  neckDiagram?: { id: string; shareId: string; title: string } | null;
  groove: { id: string; title: string; query: string; token: string } | null;
  /** A preconfigured metronome assignment, where this task owns one. */
  metronome: MetronomePracticePreset | null;
  /**
   * The instrument of the lesson this task came from, when the task has one.
   * Optional: an older API sends none, and absent means "not confirmed".
   */
  instrument?: string | null;
}

interface PracticeTodoList {
  todos: PracticeTodo[];
}

export interface StaffPracticeTodoHistoryPage {
  todos: PracticeTodo[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

function studentPath(prefix: "/me" | "/admin", studentId: string): string {
  return `${prefix}/students/${encodeURIComponent(studentId)}/practice-todos`;
}

export async function getFamilyPracticeTodos(studentId: string): Promise<PracticeTodo[]> {
  return (await get<PracticeTodoList>(studentPath("/me", studentId))).todos;
}

export async function setFamilyPracticeTodoCompleted(
  studentId: string,
  todoId: string,
  completed: boolean,
): Promise<PracticeTodo> {
  const response = await send<{ todo: PracticeTodo }>(
    "PATCH",
    `${studentPath("/me", studentId)}/${encodeURIComponent(todoId)}`,
    { completed },
  );
  return response.todo;
}

export async function saveAssignedMetronomePractice(
  studentId: string,
  todoId: string,
  input: { startedAt: string; stoppedAt: string; endBpm: number },
): Promise<{ clamped: boolean }> {
  return send(
    "POST",
    `${studentPath("/me", studentId)}/${encodeURIComponent(todoId)}/metronome`,
    input,
  );
}

export async function getStaffPracticeTodos(studentId: string): Promise<PracticeTodo[]> {
  return (await get<PracticeTodoList>(studentPath("/admin", studentId))).todos;
}

/** Current work is a separate, uncapped working set for the staff record. */
export async function getStaffCurrentPracticeTodos(studentId: string): Promise<PracticeTodo[]> {
  return (
    await get<PracticeTodoList>(
      `${studentPath("/admin", studentId)}/current`,
    )
  ).todos;
}

/** Completed/withdrawn work is historical and arrives in small pages. */
export async function getStaffPracticeTodoHistory(
  studentId: string,
  page = 1,
  limit = 10,
): Promise<StaffPracticeTodoHistoryPage> {
  return get<StaffPracticeTodoHistoryPage>(
    `${studentPath("/admin", studentId)}/history`,
    { page: String(page), limit: String(limit) },
  );
}

export async function addStaffPracticeTodo(
  studentId: string,
  input: {
    title: string;
    description?: string | null;
    repertoireId?: string | null;
    sourceLessonId?: string | null;
    requestId?: string;
    suggestedMinutes?: number | null;
    metronome?: MetronomePracticePreset | null;
  },
): Promise<PracticeTodo> {
  const response = await send<{ todo: PracticeTodo }>(
    "POST",
    studentPath("/admin", studentId),
    input,
  );
  return response.todo;
}

export async function withdrawStaffPracticeTodo(
  studentId: string,
  todoId: string,
): Promise<void> {
  await send<void>(
    "DELETE",
    `${studentPath("/admin", studentId)}/${encodeURIComponent(todoId)}`,
  );
}
